/**
 * PATH: packages/bills/src/components/DocxBlobViewer.tsx
 * FILE_ID: BILLS-PKG-CMP-DOCX-001
 * PURPOSE: Render a `.docx` Blob as readable HTML inside the bill viewer.
 *
 * WHY: The default DocumentViewer can show PDFs and images natively but
 *      not Word docs — bills frequently arrive as .docx (vendor-supplied
 *      billing matrices, supplier statements). Asking users to download
 *      every Word bill to see it kills throughput. Browsers don't render
 *      .docx, so we convert it to HTML on-the-fly.
 *
 *      Conversion is dependency-injected via `BillsClient.convertDocxToHtml`
 *      so this package stays zero-runtime-dep. The host app (apps/sam)
 *      lazy-imports `mammoth` only when a Word bill is opened, keeping
 *      the main bundle lean.
 *
 *      We sanitise the converted HTML with `client.sanitiseHtml` (DOMPurify
 *      in both apps) before rendering — the converter is a renderer, not
 *      a security boundary. If sanitiseHtml is missing we refuse to
 *      render and tell the user to download the original.
 *
 * FLOW:
 *   ┌──────┐  fetchAttachmentBlob  ┌──────┐  convertDocxToHtml  ┌──────┐  sanitiseHtml  ┌──────┐
 *   │ bill │ ─────────────────────▶│ blob │ ───────────────────▶│ html │ ──────────────▶│ DOM  │
 *   └──────┘                       └──────┘                     └──────┘                └──────┘
 *
 * DEPENDENCIES:
 *   - react: state + effect for the conversion lifecycle
 *   - useBillsClient: read injected converter + sanitiser
 *
 * RELATED:
 *   - BILLS-PKG-CMP-DOC-001 (DocumentViewer.tsx — orchestrator)
 *   - apps/sam billsClient.ts (convertDocxToHtml impl)
 */

import { useEffect, useMemo, useState } from 'react';
import { useBillsClient } from '../client';

interface Props {
  /** Source `.docx` bytes. Provided by `DocumentViewer` after blob fetch. */
  blob: Blob | null;
  /**
   * Zoom percentage (e.g. 100 == native size). Applied via CSS scale on
   * the rendered prose, mirroring the image viewer's behaviour so users
   * get consistent zoom UX across all preview formats.
   */
  zoom: number;
  /** Used to reset internal state when the user picks a different bill. */
  cacheKey: string | null;
  /** Filename for the "Download original" fallback link. */
  filename: string;
  /** Direct URL for "Download original" — typically the staticUrl. */
  downloadUrl: string | null;
}

interface ConversionState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  html: string;
  error: string | null;
}

const INITIAL_STATE: ConversionState = {
  status: 'idle',
  html: '',
  error: null,
};

export function DocxBlobViewer({ blob, zoom, cacheKey, filename, downloadUrl }: Props) {
  const client = useBillsClient();
  const [state, setState] = useState<ConversionState>(INITIAL_STATE);

  // Reset state whenever the user picks a different bill — otherwise we
  // briefly flash the previous bill's HTML before the new conversion
  // resolves. Keying off `cacheKey` (bill.id) is more reliable than the
  // blob identity, which can change between fetches even for the same
  // attachment.
  useEffect(() => {
    setState(INITIAL_STATE);
  }, [cacheKey]);

  useEffect(() => {
    let cancelled = false;
    if (!blob) return;
    const convert = client.convertDocxToHtml;
    if (typeof convert !== 'function') {
      setState({
        status: 'error',
        html: '',
        error:
          'Word preview is not configured for this app. Download the file to view.',
      });
      return;
    }

    setState({ status: 'loading', html: '', error: null });

    convert(blob)
      .then((result) => {
        if (cancelled) return;
        const sanitised =
          typeof client.sanitiseHtml === 'function'
            ? client.sanitiseHtml(result.html)
            : '';
        if (!sanitised) {
          // Either the host didn't inject DOMPurify, or sanitisation
          // emptied a malformed payload. Refuse to render unsanitised
          // HTML — small risk of cross-site-scripting from a hostile
          // .docx outweighs the convenience.
          setState({
            status: 'error',
            html: '',
            error:
              typeof client.sanitiseHtml === 'function'
                ? 'Word document was empty or could not be rendered safely.'
                : 'HTML sanitiser not configured. Download the file to view.',
          });
          return;
        }
        setState({ status: 'ready', html: sanitised, error: null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : 'Conversion failed';
        setState({ status: 'error', html: '', error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [blob, client]);

  // Memoise the rendered HTML node so we don't re-create the DOM on every
  // zoom change — only on actual content change. The zoom lives in a
  // wrapping `style.transform`, which is layout-cheap.
  const renderedHtml = useMemo(
    () => ({ __html: state.html }),
    [state.html],
  );

  if (state.status === 'loading' || state.status === 'idle') {
    return <DocxEmpty>Converting Word document…</DocxEmpty>;
  }

  if (state.status === 'error') {
    return (
      <DocxEmpty>
        <p>Could not preview Word document.</p>
        <p className="mt-1 text-xs text-destructive">{state.error}</p>
        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            download={filename}
            className="mt-2 inline-block text-primary underline"
          >
            Download original
          </a>
        )}
      </DocxEmpty>
    );
  }

  return (
    <div className="flex h-full w-full justify-center overflow-auto rounded-md border border-border bg-background">
      <div
        className="docx-prose px-8 py-6"
        style={{
          width: '100%',
          maxWidth: '900px',
          transform: `scale(${zoom / 100})`,
          transformOrigin: 'top center',
          transition: 'transform 80ms ease-out',
        }}
        // Sanitised above via client.sanitiseHtml; safe to dangerouslySet.
        dangerouslySetInnerHTML={renderedHtml}
      />
    </div>
  );
}

function DocxEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted/30 p-6 text-center text-sm text-muted-foreground">
      <div>{children}</div>
    </div>
  );
}
